import { useReducer, useCallback, useEffect } from "react";
import toast from "react-hot-toast";
import {
  useV3Create,
  useV3Update,
  useV3DeleteById,
  useV3GenericGetData,
  GenericPath,
  useV3UploadDocument,
  useV3DocumentDownload,
} from "src/api/snugtotal/estate_app";
import type { File } from "src/components/file-dropzone";

import { UtilitiesObj } from "src/types/snugtotal";
import { downloadFileFromS3 } from "src/utils/snug";
import { reportError } from "src/utils/error-reporting";
import { useMounted } from "../use-mounted";

// Action Types
type UtilitiesStoreAction =
  | { type: "SET_UTILITIES"; payload: UtilitiesObj[] }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "ADD_OR_UPDATE_UTILITIES"; payload: UtilitiesObj }
  | { type: "DELETE_UTILITIES"; payload: string }
  | { type: "SET_UTILITIES_FILES_TO_UPLOAD"; payload: File[] }
  | { type: "SET_UTILITIES_SAVING_NEW"; payload: boolean };

// Reducer Function
const utilitiesStoreReducer = (
  state: UtilitiesStoreState,
  action: UtilitiesStoreAction
): UtilitiesStoreState => {
  switch (action.type) {
    case "SET_UTILITIES":
      return {
        ...state,
        utilities: action.payload,
        utilitiesLoading: false,
      };

    case "SET_LOADING":
      return { ...state, utilitiesLoading: action.payload };

    case "ADD_OR_UPDATE_UTILITIES":
      const index = state.utilities.findIndex(
        (l) => l.id === action.payload.id
      );
      let updatedUtilities = [...state.utilities];
      if (index > -1) {
        updatedUtilities[index] = action.payload;
      } else {
        updatedUtilities.push(action.payload);
      }
      return { ...state, utilities: updatedUtilities };

    case "DELETE_UTILITIES":
      return {
        ...state,
        utilities: state.utilities.filter((l) => l.id !== action.payload),
      };
    case "SET_UTILITIES_FILES_TO_UPLOAD":
      return { ...state, utilitiesFilestoUpload: action.payload };
    case "SET_UTILITIES_SAVING_NEW":
      return { ...state, utilitiesSavingNew: action.payload };
    default:
      return state;
  }
};

// Initial State
interface UtilitiesStoreState {
  utilities: UtilitiesObj[];
  utilitiesLoading: boolean;
  utilitiesSavingNew: boolean;
  utilitiesFilestoUpload: File[];
}

// Hook
export const useUtilitiesStore = (ud_id: string | null) => {
  
  const isMounted = useMounted();
  const [state, dispatch] = useReducer(utilitiesStoreReducer, {
    utilities: [],
    utilitiesLoading: true,
    utilitiesFilestoUpload: [],
    utilitiesSavingNew: false,
  });

  const deleteUtilitiesById = useV3DeleteById(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const fetchUtilitiesData = useV3GenericGetData<UtilitiesObj[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const createUtilities = useV3Create<UtilitiesObj>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );
  const updateUtilities = useV3Update<UtilitiesObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );
  const downloadDocument = useV3DocumentDownload(
    GenericPath.GENERIC_DOCUMENT_DOWNLOAD_V3
  );
  const uploadDocument = useV3UploadDocument<UtilitiesObj>(
    GenericPath.GENERIC_DOCUMENT_CREATE_V3
  );
  const deleteDocumentById = useV3DeleteById(
    GenericPath.GENERIC_DOCUMENT_DELETE_V3
  );

  /// DOCUMENTS

  const handleUtilitiesFilesUpload = useCallback(
    async (entityId: string) => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      const files = state.utilitiesFilestoUpload;
      let remaining = files;
      const totalFiles = files.length;
      let successCount = 0;
      let failCount = 0;
      for (let index = 0; index < totalFiles; index++) {
        const currentFile = files[index];

        try {
          const res = await uploadDocument(
            {
              ud_id: ud_id,
              entity: "utilities",
              entity_id: entityId,
            },
            currentFile
          );
          if (res) {
            successCount++;

            // update state
            dispatch({
              type: "ADD_OR_UPDATE_UTILITIES",
              payload: res,
            });

            remaining = remaining.filter(
              (_file) => _file.path !== currentFile.path
            );
            dispatch({
              type: "SET_UTILITIES_FILES_TO_UPLOAD",
              payload: remaining,
            });

            const successMessage = `${successCount} of ${totalFiles} files uploaded successfully`;
            toast.success(successMessage);
          } else {
            failCount++;
            const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
            // Call your toast function here, e.g:
            toast.error(errorMessage);
            reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId}`));
          }
        } catch (error) {
          failCount++;
          const errorMessage = `${failCount} of ${totalFiles} files failed to upload`;
          // Call your toast function here, e.g:
          toast.error(errorMessage);

          console.error(errorMessage);
          reportError(new Error(`Document Error: Failed to upload document for entity_id: ${entityId} with message "${error}"`));
        }
      }
    },
    [state.utilitiesFilestoUpload, uploadDocument, ud_id]
  );

  const handleDownloadUtilitiesDocument = async (
    utilities_id: string,
    document_id: string | null
  ) => {
    if (!document_id || !utilities_id || !ud_id) {
      return Promise.reject("Missing required parameters");
    }
    toast.success("Downloading file...");

    const params: any = {
      document_id: document_id,
      entity: "utilities",
      entity_id: utilities_id,
      ud_id: ud_id,
    };

    downloadDocument(params)
      .then((res: any) => {
        if (res) {
          // download File from s3 res and get file name from mathcing document id in utilities objects' documents array
          downloadFileFromS3(
            res,
            state.utilities
              ?.find((p) => p.id === utilities_id)
              ?.documents?.find((d) => d.document_id === document_id)
              ?.document_name || "Snug_Download.error"
          );
        } else {
          reportError(new Error(`Document Error: Failed to get document with message "No response"`));
        }
      })
      .catch((err) => {
        toast.error("Failed to download file");
        reportError(new Error(`Document Error: Failed to get document with message "${err}"`));
      });
  };

  const handleDeleteUtilitiesDocument = useCallback(
    async (utilities_id: string, document_id: string | null): Promise<void> => {
      if (!document_id || !utilities_id || !ud_id) {
        return Promise.reject("Missing required parameters");
      }
      
      try {
        dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: true });

        await deleteDocumentById({
          ud_id: ud_id,
          entity: "utilities",
          entity_id: utilities_id,
          document_id: document_id,
        });
        if (isMounted()) {
          // remove the document object formm the document array in the utilities object
          const updatedUtilities = state.utilities.map((p) => {
            if (p.id === utilities_id) {
              return {
                ...p,
                documents: p.documents
                  ? p.documents.filter((d) => d.document_id !== document_id)
                  : [],
              };
            }
            return p;
          });
          dispatch({
            type: "SET_UTILITIES",
            payload: updatedUtilities,
          });
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to delete document");
        throw err;
      } finally {
        if (isMounted()) {
          dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: false });
        }
      }
    },
    [isMounted, state.utilities, deleteDocumentById, ud_id]
  );

  const handleUtilitiesFilesDrop = useCallback(
    (newFiles: File[]): void => {
      dispatch({
        type: "SET_UTILITIES_FILES_TO_UPLOAD",
        payload: [...state.utilitiesFilestoUpload, ...newFiles],
      }); //this may not be needed
    },
    [state.utilitiesFilestoUpload]
  );

  const handleUtilitiesFileRemove = useCallback(
    (file: File): void => {
      const updatedFiles = state.utilitiesFilestoUpload.filter(
        (_file) => _file.path !== file.path
      );
      dispatch({
        type: "SET_UTILITIES_FILES_TO_UPLOAD",
        payload: updatedFiles,
      });
    },
    [state.utilitiesFilestoUpload]
  );

  const handleUtilitiesFilesRemoveAll = useCallback((): void => {
    dispatch({ type: "SET_UTILITIES_FILES_TO_UPLOAD", payload: [] });
  }, []);

  /// END DOCUMENTS

  const fetchUtilitiesItems = useCallback(
    async (ud_id: string) => {
      dispatch({ type: "SET_LOADING", payload: true });
      try {
        const response = await fetchUtilitiesData({
          ud_id: ud_id, //bleh. REFACTOR IN v3
          entity: "utilities",
        });

        if (isMounted()) {
          dispatch({ type: "SET_UTILITIES", payload: response });
        }
      } catch (error) {
        console.error(error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchUtilitiesData, isMounted]
  );

  const handleAddOrUpdateUtilitiesSingleCall = useCallback(
    async (utilitiesItem: UtilitiesObj): Promise<UtilitiesObj | null> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      try {
        const isUpdate = Boolean(utilitiesItem.id);
        let response;

        if (isUpdate) {
          // Update logic
          response = await updateUtilities(
            {
              ud_id: ud_id,
              entity: "utilities",
              entity_id: utilitiesItem.id || undefined,
            },
            utilitiesItem,
            true // Assuming you want to perform a partial update
          );
        } else {
          // Create logic
          response = await createUtilities(
            { ud_id: ud_id, entity: "utilities" }, // Adjust according to your actual API parameter requirements
            utilitiesItem
          );
        }
        dispatch({ type: "ADD_OR_UPDATE_UTILITIES", payload: response });
        return response;
      } catch (error) {
        toast.error("Failed to save utilities item");
        console.error(error);
        return null;
      }
    },
    [ud_id, createUtilities, updateUtilities]
  );

  const handleAddOrUpdateUtilities = useCallback(
    async (utilitiesItem: UtilitiesObj) => {
      dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: true });
      if (state.utilitiesFilestoUpload.length > 0) {
        try {
          const res = await handleAddOrUpdateUtilitiesSingleCall(utilitiesItem);
          if (res) {
            await handleUtilitiesFilesUpload(res.id || "");
            return res;
          }
          return null;
        } catch (error) {
          toast.error(
            "Failed to add or edit Utilities data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: false });
        }
      } else {
        try {
          return handleAddOrUpdateUtilitiesSingleCall(utilitiesItem);
        } catch (error) {
          toast.error(
            "Failed to add or edit Utilities data. Please try again."
          );
          console.error(error);
          throw error;
        } finally {
          dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: false });
        }
      }
    },
    [
      handleAddOrUpdateUtilitiesSingleCall,
      state.utilitiesFilestoUpload,
      handleUtilitiesFilesUpload,
    ]
  );

  const handleDeleteUtilities = useCallback(
    async (id: string): Promise<void> => {
      if (!ud_id) {
        return Promise.reject("Missing ud_id");
      }

      dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: true });
      try {
        if (!ud_id) {
          return Promise.reject("No ud_id provided");
        }
        if (!id) {
          return Promise.reject("No id provided");
        }
        await deleteUtilitiesById({
          ud_id: ud_id,
          entity: "utilities",
          entity_id: id || undefined,
        });
        dispatch({ type: "DELETE_UTILITIES", payload: id });
      } catch (error) {
        toast.error("Failed to delete utilities item");
        console.error(error);
      } finally {
        dispatch({ type: "SET_UTILITIES_SAVING_NEW", payload: false });
      }
    },
    [deleteUtilitiesById, ud_id]
  );

  useEffect(() => {
    if (ud_id) {
      fetchUtilitiesItems(ud_id); // Fetch all utilities items
    }
  }, [ fetchUtilitiesItems, ud_id]);

  return {
    utilities: state.utilities,
    utilitiesLoading: state.utilitiesLoading,
    utilitiesSavingNew: state.utilitiesSavingNew,
    utilitiesFilestoUpload: state.utilitiesFilestoUpload,
    handleAddOrUpdateUtilities,
    handleDeleteUtilities,
    handleUtilitiesFileRemove,
    handleUtilitiesFilesDrop,
    handleUtilitiesFilesRemoveAll,
    handleDownloadUtilitiesDocument,
    handleDeleteUtilitiesDocument,
    handleUtilitiesFilesUpload,
  };
};
